import { useEffect, useState, useRef } from 'react'

import { EditorState, EditorSelection, Prec } from '@codemirror/state'
import { EditorView, keymap, placeholder, Decoration, DecorationSet, ViewPlugin, ViewUpdate } from '@codemirror/view'
import { indentWithTab } from '@codemirror/commands'
import { markdown, markdownLanguage } from '@codemirror/lang-markdown'
import { languages } from '@codemirror/language-data'
import { oneDark } from '@codemirror/theme-one-dark'
import { basicSetup } from 'codemirror'
import { dom } from '@uiw/codemirror-extensions-events'
import { useSelector } from 'react-redux'

export const transparentTheme = EditorView.theme({
  '&': {
    backgroundColor: 'transparent !important',
    height: '100%'
  }
})

// Placeholder Widget 类
class PlaceholderWidget {
  constructor(text) {
    this.text = text
  }

  toDOM() {
    const span = document.createElement('span')
    span.className = 'cm-line-placeholder'
    span.textContent = this.text
    span.style.color = '#999'
    span.style.fontStyle = 'italic'
    span.style.pointerEvents = 'none'
    span.style.userSelect = 'none'
    return span
  }

  eq(other) {
    return other instanceof PlaceholderWidget && other.text === this.text
  }

  updateDOM() {
    return false
  }

  get estimatedHeight() {
    return -1
  }

  ignoreEvent() {
    return true
  }

  // 必需的方法
  coordsAt() {
    return null
  }

  destroy() {
    // 清理资源（如果需要）
  }

  compare(other) {
    return this.eq(other)
  }
}

// 动态 placeholder 插件
const dynamicLinePlaceholder = (placeholderText = "Type '/' for commands, '@ai' for AI Assist") => {
  return ViewPlugin.fromClass(
    class {
      constructor(view) {
        this.decorations = this.buildDecorations(view)
      }

      update(update) {
        if (update.docChanged || update.selectionSet) {
          this.decorations = this.buildDecorations(update.view)
        }
      }

      buildDecorations(view) {
        const widgets = []
        const { state } = view
        const { selection } = state

        // 确保只有一个光标且没有选中内容
        if (selection.ranges.length !== 1 || !selection.main.empty) {
          return Decoration.none
        }

        // 获取当前光标所在的行号
        const cursorLine = state.doc.lineAt(selection.main.head)

        // 核心修复：检查行是否完全为空（长度为 0）
        // 而不是使用 trim() 判断
        if (cursorLine.length === 0) {
          // 确保光标在行首
          if (selection.main.head === cursorLine.from) {
            const widget = Decoration.widget({
              widget: new PlaceholderWidget(placeholderText),
              side: 1, // 显示在光标右侧
            })

            widgets.push(widget.range(cursorLine.from))
          }
        }

        return Decoration.set(widgets)
      }
    },
    {
      decorations: (v) => v.decorations,
    }
  )
}

// '@ai' 提示插件 - 当用户输入 '@ai' 时显示 "press Enter" 提示
const aiTriggerHint = () => {
  return ViewPlugin.fromClass(
    class {
      constructor(view) {
        this.decorations = this.buildDecorations(view)
      }

      update(update) {
        if (update.docChanged || update.selectionSet) {
          this.decorations = this.buildDecorations(update.view)
        }
      }

      buildDecorations(view) {
        const widgets = []
        const { state } = view
        const { selection } = state

        // 确保只有一个光标且没有选中内容
        if (selection.ranges.length !== 1 || !selection.main.empty) {
          return Decoration.none
        }

        // 获取当前光标所在的行
        const cursorPos = selection.main.head
        const line = state.doc.lineAt(cursorPos)
        const lineText = line.text.trim()

        // 检查当前行是否以 '@ai' 结尾（不区分大小写）
        if (lineText.toLowerCase().endsWith('@ai')) {
          // 确保光标在行尾或行尾附近（允许尾随空格）
          if (cursorPos >= line.to - (line.text.length - line.text.trimEnd().length)) {
            const widget = Decoration.widget({
              widget: new PlaceholderWidget(' ← Prompt + Enter'),
              side: 1, // 显示在光标右侧
            })

            widgets.push(widget.range(cursorPos))
          }
        }

        return Decoration.set(widgets)
      }
    },
    {
      decorations: (v) => v.decorations,
    }
  )
}

const useCodeMirror = (props) => {
  const refContainer = useRef(null)
  const [editorView, setEditorView] = useState()
  const {
    onChange,
    onInput,
    onClick,
    initialDoc,
    editorReadyCallback,
    onEnterKey,
    shouldBlockEnter,
    dynamicPlaceholderText // 新增：可自定义动态 placeholder 文本
  } = props
  const onEnterKeyRef = useRef(onEnterKey)
  const shouldBlockEnterRef = useRef(shouldBlockEnter)
  const shouldUseDarkColors = useSelector(state => state.uiState.should_use_dark_colors)

  // 更新 onEnterKey ref
  useEffect(() => {
    onEnterKeyRef.current = onEnterKey
  }, [onEnterKey])

  // 更新 shouldBlockEnter ref
  useEffect(() => {
    shouldBlockEnterRef.current = shouldBlockEnter
  }, [shouldBlockEnter])

  const domEvent = dom({
    click: (event) => {
      onClick && onClick(event)
    },
  })

  useEffect(() => {
    if (!refContainer.current) return

    const startState = EditorState.create({
      doc: initialDoc?.markdown,

      selection: EditorSelection.create([
        EditorSelection.range(0, 0),
        EditorSelection.cursor(0)
      ]),

      extensions: [
        basicSetup,
        domEvent,
        markdown({
          base: markdownLanguage,
          codeLanguages: languages,
          addKeymap: true
        }),
        shouldUseDarkColors ? oneDark : undefined,
        // 保留原有的全局 placeholder（当编辑器完全为空时显示）
        placeholder(`Type '/' for commands, '@ai' for AI Assist`),
        // 新增：动态行级 placeholder（光标在空行时显示）
        dynamicLinePlaceholder(dynamicPlaceholderText || "Type '/' for commands, '@ai' for AI Assist"),
        // 新增：'@ai' 触发提示（当用户输入 '@ai' 时显示 "press Enter" 提示）
        aiTriggerHint(),
        EditorView.lineWrapping,
        EditorView.updateListener.of(update => {
          if (update.changes) {
            onChange && onChange(update.state)
          }
        }),
        EditorView.inputHandler.of(onInput),
        // 添加 Tab 键缩进功能
        keymap.of([indentWithTab]),
        Prec.highest(
          keymap.of([
            {
              key: "ArrowDown",
              run: (view) => {
                return true
              }
            },
            {
              key: "ArrowUp",
              run: (view) => {
                return true
              }
            },
            {
              key: "Enter",
              run: (view) => {
                // 首先检查是否触发 '@ai'
                if (onEnterKeyRef.current) {
                  const handled = onEnterKeyRef.current(view)
                  if (handled) {
                    return true // '@ai' 已处理，阻止默认行为
                  }
                }

                // 当 slash menu 或 AI dialog 可见时，屏蔽 Enter 键
                const shouldBlock = shouldBlockEnterRef.current && shouldBlockEnterRef.current()
                if (shouldBlock) {
                  return true // 阻止默认行为
                }
                return false // 允许默认行为
              }
            }
          ])
        ),
        transparentTheme
      ].filter(ext => !!ext)
    })

    const view = new EditorView({
      state: startState,
      parent: refContainer.current
    })
    setEditorView(view)

    !!editorReadyCallback && editorReadyCallback(view)

    if (document.getElementsByClassName('cm-editor')[0]) {
      document.getElementsByClassName('cm-editor')[0].style.outline = 'none'
    }

    return () => {
      view.destroy()
    }
  }, [refContainer, initialDoc?.hid, initialDoc?.nodeId, shouldUseDarkColors, dynamicPlaceholderText])

  return [refContainer, editorView]
}

export default useCodeMirror